import { lazy, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { SecondaryButton } from '@pulse-web-ui/button-v2';
import { Tooltip, type TooltipProps } from '@pulse-web-ui/tooltip';

import {
  ColumnContainer,
  LabelValue,
  RowContainer,
  type SectionData,
  type SectionValue,
  TextLink,
} from '@shared/index';

import { SectionHeader, SectionWrapper } from './section.styles';

const Edit2Icon = lazy(() => import('@pulse-web-ui/icons/lib/lazy/edit-2'));

type SecondaryButtonProps = React.ComponentProps<typeof SecondaryButton>;

interface SectionProps<T extends SectionData> {
  data: T;
  dataToCompare?: T;
  dataLabels: Record<string, string>;
  header?: {
    title?: string;
    titleTooltipProps?: TooltipProps;
    buttonProps?: SecondaryButtonProps;
    fontSize?: string;
    marginBottom?: number;
  };
}

export const Section = <T extends SectionData>({
  data,
  dataToCompare,
  dataLabels,
  header,
}: SectionProps<T>) => {
  const { t } = useTranslation();

  const { title, buttonProps, titleTooltipProps, fontSize, marginBottom } =
    header || {};

  const mergedButtonProps: SecondaryButtonProps | null = useMemo(() => {
    if (buttonProps && Object.keys(buttonProps).length) {
      return {
        ...buttonProps,
        color: 'normal',
        size: 'xs',
        iconPosition: 'left',
        icon: (
          <Edit2Icon
            width={24}
            viewBox='0 0 24 24'
          />
        ),
      };
    }
    return null;
  }, [buttonProps]);

  const getValue = (sectionValue?: SectionValue) =>
    typeof sectionValue === 'string' ? sectionValue : sectionValue?.value;

  return (
    <SectionWrapper>
      {title && (
        <SectionHeader
          $fontSize={fontSize}
          $marginBottom={marginBottom}
        >
          <RowContainer
            $gap={8}
            $marginBottom={0}
          >
            {title}
            {titleTooltipProps && <Tooltip {...titleTooltipProps} />}
          </RowContainer>

          {mergedButtonProps && (
            <SecondaryButton {...mergedButtonProps}>
              {t('COMMON:buttons.edit')}
            </SecondaryButton>
          )}
        </SectionHeader>
      )}
      <ColumnContainer $gap={16}>
        {Object.keys(data).map((key) => {
          const label = dataLabels[key];
          const sectionValue = data[key];
          const value = getValue(sectionValue);

          const isHightlight = dataToCompare
            ? value !== getValue(dataToCompare?.[key])
            : false;

          if (value) {
            return typeof sectionValue === 'object' && sectionValue.isLink ? (
              <TextLink
                key={key}
                href={value}
                target='_blank'
              >
                <LabelValue
                  isWithDesktopStyles
                  isHightlight={isHightlight}
                  label={label}
                  value={value}
                />
              </TextLink>
            ) : (
              <div key={key}>
                <LabelValue
                  isWithDesktopStyles
                  isHightlight={isHightlight}
                  label={label}
                  value={value}
                />
              </div>
            );
          }
        })}
      </ColumnContainer>
    </SectionWrapper>
  );
};
